import { useEffect, useState } from 'react';
import { App, Card, Checkbox, Flex, Input, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import { useGetPlans } from '@/features/agreements/hooks/usePlans';
import {
  coverageConfigQueryKeys,
  useDeleteCoverageConfig,
  useGetCoverageConfigs,
  useUpdateCoverageConfig,
} from '@/features/quotations/hooks/useCoverageConfigs';
import { getCoverageConfigs } from '@/features/quotations/services/coverageConfigService';
import { useModal } from '@/shared/hooks/useModal';
import { EmptyStateCard } from '../EmptyStateCard';
import { SectionHeader } from '../SectionHeader';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';
import { AddCoveragesDrawer } from './AddCoveragesDrawer';
import { AddCoveragesButton } from './AddCoveragesButton';

const { Text } = Typography;

export const CoveragesStep = ({ agreementId, onNextRef, readOnly = false }) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [activePlanKey, setActivePlanKey] = useState(null);
  const [localEdits, setLocalEdits] = useState(() => ({}));
  const addCoveragesDrawer = useModal(null);

  const plansQuery = useGetPlans({ agreementId });
  const plans = plansQuery.data ?? [];
  const hasPlans = plans.length > 0;

  const activeConfigsQuery = useGetCoverageConfigs({ planId: activePlanKey, active: true }, { enabled: !!activePlanKey });
  const activePlanConfigs = activeConfigsQuery.data ?? [];

  // Obtener coberturas de todos los planes para calcular el progreso
  const allPlansConfigsQueries = useQueries({
    queries: plans.map((plan) => ({
      queryKey: coverageConfigQueryKeys.list({ planId: plan.id, active: true }),
      queryFn: () => getCoverageConfigs({ planId: plan.id, active: true }),
      enabled: hasPlans,
    })),
  });

  const updateConfigMutation = useUpdateCoverageConfig();
  const deleteConfigMutation = useDeleteCoverageConfig();

  // Calcular planes con coberturas
  const plansWithCoverages = allPlansConfigsQueries.reduce((count, query) => {
    if (query.data && query.data.length > 0) {
      return count + 1;
    }
    return count;
  }, 0);

  const progressLabel = hasPlans
    ? `Coberturas de planes completos: ${plansWithCoverages}/${plans.length} planes`
    : 'Selecciona un plan';

  const handleNext = async () => {
    if (readOnly) return;
    if (!hasPlans) return;
    const configsPerPlan = await Promise.all(plans.map((plan) => getCoverageConfigs({ planId: plan.id, active: true })));
    const allHaveCoverages = configsPerPlan.every((list) => list.length > 0);
    if (!allHaveCoverages) {
      notification.error({
        message: <Text strong>Completa las coberturas de todos los planes</Text>,
        description: 'Agrega al menos una cobertura a cada plan para continuar.',
      });
      return false;
    }

    const configsToUpdate = [];
    for (let i = 0; i < plans.length; i++) {
      const planId = plans[i].id;
      const configs = configsPerPlan[i];
      for (const config of configs) {
        if (localEdits[config.id]) {
          const merged = { ...config, ...localEdits[config.id] };
          configsToUpdate.push({
            id: config.id,
            planId,
            data: {
              coverageId: config.coverage?.id,
              limit: merged.limit ?? null,
              limitIsInsuredValue: !!merged.limitIsInsuredValue,
              deductible: merged.deductible ?? null,
            },
          });
        }
      }
    }

    if (configsToUpdate.length > 0) {
      try {
        await Promise.all(
          configsToUpdate.map(({ id, data }) => updateConfigMutation.mutateAsync({ id, data }))
        );
        const planIdsToInvalidate = [...new Set(configsToUpdate.map((u) => u.planId))];
        for (const planId of planIdsToInvalidate) {
          void queryClient.invalidateQueries({
            queryKey: coverageConfigQueryKeys.list({ planId, active: true }),
          });
        }
        setLocalEdits((prev) => {
          const next = { ...prev };
          configsToUpdate.forEach((u) => delete next[u.id]);
          return next;
        });
      } catch (error) {
        notification.error({
          message: <Text strong>Error al actualizar coberturas</Text>,
          description: getErrorMessage(error),
        });
        return false;
      }
    }
  };

  const handleAddCoverages = () => {
    addCoveragesDrawer.open(null);
  };

  const handleRemoveConfig = (planId, configId) => {
    deleteConfigMutation.mutate(
      { id: configId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: coverageConfigQueryKeys.list({ planId, active: true }),
          });
          notification.success({
            message: <Text strong>Cobertura eliminada</Text>,
            description: 'La cobertura se ha eliminado del plan.',
          });
        },
        onError: (error) => {
          notification.error({
            message: <Text strong>Error al eliminar</Text>,
            description: getErrorMessage(error),
          });
        },
      }
    );
  };

  const handleLocalConfigChange = (planId, configId, field, value) => {
    setLocalEdits((prev) => ({
      ...prev,
      [configId]: {
        ...(prev[configId] ?? {}),
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    if (!onNextRef) return;
    onNextRef.current = handleNext;
    return () => {
      onNextRef.current = null;
    };
  }, [handleNext, onNextRef]);

  useEffect(() => {
    if (hasPlans && activePlanKey === null && plans[0]) {
      setActivePlanKey(plans[0].id);
    }
  }, [hasPlans, plans, activePlanKey]);

  if (plansQuery.isLoading) return <LoadingIndicator />;
  if (plansQuery.isError) return <ErrorMessage error={plansQuery.error} />;

  if (!hasPlans) {
    return (
      <Flex justify='center' style={{ paddingInline: '5%' }}>
        <Flex vertical gap='large' flex={1}>
          <SectionHeader
            title='Coberturas del plan'
            description='Agrega coberturas y define límites y deducibles.'
          />
          <Card style={{ backgroundColor: '#f5f5f5', borderColor: '#d9d9d9' }}>
            <Flex vertical gap='middle' justify='center' align='center' style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: '1.1em' }}>
                No hay planes definidos
              </Text>
              <Text type='secondary' style={{ maxWidth: '23em' }}>
                Crea al menos un plan en el paso anterior para configurar coberturas.
              </Text>
            </Flex>
          </Card>
        </Flex>
      </Flex>
    );
  }

  const tabItems = plans.map((plan) => {
    const isActiveTab = plan.id === activePlanKey;
    const rawPlanConfigs = isActiveTab ? activePlanConfigs : [];
    const planConfigs = rawPlanConfigs.map((c) => ({ ...c, ...(localEdits[c.id] ?? {}) }));
    const mainConfigs = planConfigs.filter((c) => c.coverage?.coverageType === 'main');
    const assistConfigs = planConfigs.filter((c) => c.coverage?.coverageType === 'assistance');
    const otherConfigs = planConfigs.filter((c) => c.coverage?.coverageType === 'other');
    const hasCoverages = planConfigs.length > 0;
    const isLoading = isActiveTab && activeConfigsQuery.isLoading;
    const hasError = isActiveTab && activeConfigsQuery.isError;

    const inactiveTabCard = (
      <Card style={{ backgroundColor: '#fafafa', borderColor: '#d9d9d9' }}>
        <Flex
          vertical
          gap='middle'
          justify='center'
          align='center'
          style={{ textAlign: 'center' }}
        >
          <Text type='secondary' style={{ maxWidth: '20em' }}>
            Selecciona esta pestaña para ver y configurar las coberturas del plan <Text strong>{plan.name}</Text>.
          </Text>
        </Flex>
      </Card>
    );

    if (!isActiveTab) {
      return {
        key: plan.id,
        label: plan.name,
        children: inactiveTabCard,
      };
    }

    if (isLoading) {
      return {
        key: plan.id,
        label: plan.name,
        children: <LoadingIndicator />,
      };
    }

    if (hasError) {
      return {
        key: plan.id,
        label: plan.name,
        children: <ErrorMessage error={activeConfigsQuery.error} />,
      };
    }

    return {
      key: plan.id,
      label: plan.name,
      children: hasCoverages ? (
        <Flex vertical gap='large'>
          {mainConfigs.length > 0 && (
            <CoverageConfigTable
              title='Coberturas Principales'
              configs={mainConfigs}
              planId={plan.id}
              onRemove={handleRemoveConfig}
              onLocalChange={handleLocalConfigChange}
              readOnly={readOnly}
            />
          )}
          {assistConfigs.length > 0 && (
            <CoverageConfigTable
              title='Asistencias'
              configs={assistConfigs}
              planId={plan.id}
              onRemove={handleRemoveConfig}
              onLocalChange={handleLocalConfigChange}
              readOnly={readOnly}
            />
          )}
          {otherConfigs.length > 0 && (
            <CoverageConfigTable
              title='Otras coberturas'
              configs={otherConfigs}
              planId={plan.id}
              onRemove={handleRemoveConfig}
              onLocalChange={handleLocalConfigChange}
              readOnly={readOnly}
            />
          )}
        </Flex>
      ) : (
        <EmptyStateCard
          title='Aún no has añadido coberturas'
          description='Puedes agregar coberturas principales y asistencias.'
          action={!readOnly ? <AddCoveragesButton onClick={handleAddCoverages} /> : null}
        />
      ),
    };
  });

  return (
    <Flex justify='center' style={{ paddingInline: '5%' }}>
      <Flex vertical gap='large' flex={1} style={{ width: '100%' }}>
        <SectionHeader
          title='Coberturas del plan'
          description='Agrega coberturas y define límites y deducibles. Puedes aplicar valores por lote.'
        />
        <Tabs
          activeKey={activePlanKey ?? undefined}
          onChange={setActivePlanKey}
          items={tabItems}
          tabBarExtraContent={
            <Flex align='center' gap='small' wrap='wrap'>
              <Tag color={hasPlans && plansWithCoverages === plans.length ? 'success' : 'gold'}>
                {progressLabel}
              </Tag>
              {activePlanConfigs.length > 0 && !readOnly && <AddCoveragesButton onClick={handleAddCoverages} />}
            </Flex>
          }
        />
      </Flex>
      <AddCoveragesDrawer
        open={addCoveragesDrawer.isOpen}
        onClose={addCoveragesDrawer.close}
        planId={activePlanKey}
        existingIds={activePlanConfigs.map((c) => c.coverage?.id).filter(Boolean)}
      />
    </Flex>
  );
};

const CoverageConfigTable = ({ title, configs, planId, onRemove, onLocalChange, readOnly = false }) => {
  const columns = [
    {
      title: 'Cobertura',
      key: 'coverage',
      render: (_, record) => (
        <Text>
          {record.coverage?.name ?? '-'}
        </Text>
      ),
    },
    {
      title: 'Límite',
      dataIndex: 'limit',
      key: 'limit',
      width: 140,
      render: (limit, record) => (
        <Input
          value={record.limit ?? ''}
          onChange={(e) => onLocalChange(planId, record.id, 'limit', e.target.value || null)}
          placeholder={record.limitIsInsuredValue ? 'Valor asegurado' : '$0'}
          style={{ width: '100%' }}
          disabled={readOnly || !!record.limitIsInsuredValue}
        />
      ),
    },
    {
      title: 'Límite es valor asegurado',
      key: 'limitIsInsuredValue',
      width: 140,
      render: (_, record) => (
        <Checkbox
          checked={!!record.limitIsInsuredValue}
          onChange={(e) => {
            onLocalChange(planId, record.id, 'limitIsInsuredValue', e.target.checked);
            if (e.target.checked) {
              onLocalChange(planId, record.id, 'limit', null);
            }
          }}
          disabled={readOnly}
        />
      ),
    },
    {
      title: 'Deducible',
      dataIndex: 'deductible',
      key: 'deductible',
      width: 140,
      render: (deductible, record) => (
        <Input
          value={record.deductible ?? ''}
          onChange={(e) => onLocalChange(planId, record.id, 'deductible', e.target.value || null)}
          placeholder='Deducible'
          style={{ width: '100%' }}
          disabled={readOnly}
        />
      ),
    },
    ...(!readOnly
      ? [
          {
            title: 'Acciones',
            key: 'actions',
            width: 80,
            render: (_, record) => (
              <Tooltip title='Eliminar'>
                <span
                  style={{ cursor: 'pointer', color: '#ff4d4f' }}
                  onClick={() => onRemove(planId, record.id)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && onRemove(planId, record.id)}
                >
                  <DeleteOutlined />
                </span>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Text strong style={{ fontSize: '1em' }}>
        {title}
      </Text>
      <Text type='secondary' style={{ display: 'block', marginBottom: 8 }}>
        {configs.length} {configs.length === 1 ? 'cobertura' : 'coberturas'}
      </Text>
      <Table
        dataSource={configs}
        rowKey='id'
        columns={columns}
        pagination={false}
        size='small'
      />
    </div>
  );
}
